"use client"

import { useState } from 'react'
import { useAuth } from '@/components/auth/auth-provider'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { alerts } from '@/lib/alerts'
import { useRouter } from 'next/navigation'
import { Info } from 'lucide-react'

export default function SupportPage() {
  const { user } = useAuth()
  const router = useRouter()
  const [subject, setSubject] = useState('Ayuda con PocketWise')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) {
      await alerts.info('Faltan datos', 'Por favor escribe un asunto y un mensaje.')
      return
    }
    setSending(true)
    try {
      // Enviar directamente a Formspree
      const res = await fetch('https://formspree.io/f/mldwklzg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email: user?.email,
          subject: subject.trim(),
          message: message.trim(),
          user_id: user?.id,
          name: (user as any)?.user_metadata?.full_name || undefined,
        }),
      })
      // Formspree responde 200/OK con JSON {ok: true} o {errors: [...]}
      const data = await res.json().catch(() => ({}))
      if (!res.ok || (data && (data.errors?.length ?? 0) > 0)) {
        throw new Error(data?.errors?.[0]?.message || 'No se pudo enviar el mensaje')
      }
      await alerts.success('Mensaje enviado', 'Hemos recibido tu mensaje y te responderemos pronto.')
      router.push('/dashboard')
    } catch (err) {
      console.error('support send error', err)
      await alerts.error('No se pudo enviar', 'Intenta nuevamente en unos minutos.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 max-w-2xl">
        <h1 className="text-xl font-semibold mb-2">Soporte</h1>
        <p className="text-sm text-muted-foreground mb-4">Escríbenos y te responderemos a tu correo.</p>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Enviar un mensaje
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tu correo</label>
                <Input value={user?.email || ''} disabled className="bg-muted border-border text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Asunto</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Cuéntanos brevemente el tema" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Mensaje</label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe el problema, lo que esperabas, y si puedes los pasos para reproducirlo." rows={8} />
              </div>
              <div className="pt-2">
                <Button type="submit" disabled={sending} className="bg-teal-500 hover:bg-teal-600 text-slate-900">
                  {sending ? 'Enviando…' : 'Enviar'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => router.back()} className="ml-2">
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
